import { forwardRef, ReactNode } from "react";

import { Stack, StackProps, SxProps, Theme, useTheme } from "@mui/material";

import { FormDateCalendarSquare } from "./FormDateCalendarSquare";

import { FormTypography } from "@/forms-design-system/FormTypography";
import { InvalidThemeErrorMessage } from "@/forms-design-system/InvalidThemeErrorMessage";
import { mergeSx } from "@/helpers/mergeSx";
import { useMediaQuery } from "@/hooks/useMediaQuery";

export type BaseFormDateProps = StackProps & {
  date: Date;
  title: ReactNode;
  subtitle: ReactNode;
  components?: {
    iconSquare?: {
      sx?: SxProps<Theme>;
    };
    title?: {
      sx?: SxProps<Theme>;
    };
    subtitle?: {
      sx?: SxProps<Theme>;
    };
  };
};

/** Do not use this component directly. Use `SimpleFormDate`, `LabeledFormDate`, or `SimpleFormDateRange` instead. */
export const BaseFormDate = forwardRef<HTMLDivElement, BaseFormDateProps>(
  ({ date, title, subtitle, components, ...rest }, ref) => {
    const theme = useTheme();
    const { isSmallScreen } = useMediaQuery();

    if (!theme.palette.forms || !theme.palette.formsComponents?.date) {
      return <InvalidThemeErrorMessage />;
    }

    return (
      <Stack direction="row" alignItems="center" gap={2} {...rest} ref={ref}>
        <FormDateCalendarSquare date={date} flexShrink={0} sx={components?.iconSquare?.sx} />
        <Stack flexGrow={1} minWidth={0}>
          <FormTypography
            variant={isSmallScreen ? "subtitle2" : "subtitle1"}
            sx={mergeSx({ whiteSpace: "nowrap", overflowX: "hidden", textOverflow: "ellipsis" }, components?.title?.sx)}
          >
            {title}
          </FormTypography>
          <FormTypography
            variant={isSmallScreen ? "body2" : "body1"}
            sx={mergeSx(
              { whiteSpace: "nowrap", overflowX: "hidden", textOverflow: "ellipsis" },
              components?.subtitle?.sx
            )}
          >
            {subtitle}
          </FormTypography>
        </Stack>
      </Stack>
    );
  }
);

BaseFormDate.displayName = "BaseFormDate";
